import { point } from '@turf/turf';
import { useContext, useState } from 'react';
import { useParams } from 'react-router';
import { mapModes } from '../../../constants/map';
import { MapContext } from '../../../contexts/MapContext';
import { layerIsCustom } from '../../../utils/layers';
import { flyTo } from '../../../utils/map';
import LocationIcon from '../../icons/LocationIcon';
import ButtonWithLoading from '../../basics/ButtonWithLoading';
import { getCurrentPosition } from '../../../utils/geolocation';
import { captureEvent } from '../../../utils/analytics';
import { showError } from '../../../utils/toast';
import useReadonly from '../../../hooks/useReadonly';

const browserSupportsGeolocation = 'geolocation' in window.navigator;

const DropPinButton = ({ className }) => {
  const { farmId } = useParams();
  const {
    mapState: { mapMode, setMapMode, activeLayerId, activeLayer, mapRef },
    layers: { addLayerFeature },
  } = useContext(MapContext);

  const [isDroppingPin, setIsDroppingPin] = useState(false);
  const readonly = useReadonly();
  const dropPin = async () => {
    captureEvent('drop-pin');

    // We cannot be in edit mode for this, since mapbox-draw will not update its
    // state with the new feature, causing subsequent edits to overwrite the
    // dropped pin.
    if ([mapModes.EDIT_LAYER_FEATURE, mapModes.EDIT_LAYER].includes(mapMode)) {
      setMapMode(mapModes.LAYER_ENGAGED, activeLayerId);
    }

    try {
      setIsDroppingPin(true);

      // Use Geolocation API to get user's current position.
      const coords = await getCurrentPosition();

      // Add layer feature
      const [feature, promise] = addLayerFeature(
        activeLayerId,
        farmId,
        point(coords),
      );
      const success = await promise;
      setIsDroppingPin(false);
      if (!success) return;

      // select feature and go to it on map
      setMapMode(mapModes.LAYER_FEATURE_ENGAGED, feature.id);
      flyTo(feature, mapRef, { duration: 0 });
    } catch (err) {
      setIsDroppingPin(false);

      showError(err);
    }
  };

  if (!browserSupportsGeolocation || !layerIsCustom(activeLayer) || readonly) {
    return null;
  }

  return (
    <ButtonWithLoading
      onClick={dropPin}
      size="sm"
      className={className}
      color="secondary"
      outline
      loading={isDroppingPin}
    >
      Drop pin
      <LocationIcon className="ml-1" />
    </ButtonWithLoading>
  );
};

export default DropPinButton;
